/**
 * Transfer actions renderer
 */
import {
  Component,
  ViewEncapsulation,
  Injector,
  Type,
  InjectionToken,
  AbstractType,
} from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  ENTITY_TYPES,
  ITransferData,
  TRANSFER_TYPE,
  COMMON_ACTION_TYPES,
} from '@shared/types';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import { Router } from '@angular/router';
import {
  get,
  includes,
  isEmpty,
  isFunction,
  forEach,
  cloneDeep,
  isEqual,
} from 'lodash';
import {
  StateService,
  TransferService,
  IncompleteTransferService,
  PermissionService,
} from '@shared/services';
import { APIActions, UiActions } from '@store/actions';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import { TRANSFER_ACTION_BUTTONS } from '@shared/types/entities/transfer/transfer-action-buttons.enum';
import { UPDATE_STATUS_MSG_MAPPING } from '@app/shared/services/transfer/transfer-constants';
import { generateBulkUpdateConfirmMessageParam } from '@app/shared/services/transfer/transfer.util';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-incomplete-transfer-actions-renderer',
  templateUrl:
    './../transfer-actions-renderer/transfer-actions-renderer.component.html',
  styleUrls: [
    './../transfer-actions-renderer/transfer-actions-renderer.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class XVIIncompleteTransferRendererComponent
  implements AgRendererComponent
{
  @lazyInject(Router) public router: Router;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(IncompleteTransferService)
  public incompleteTransferService: IncompleteTransferService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  public data: ITransferData;
  public actionButtons: ActionButton[];

  public constructor(public injector: Injector) {}

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.incompleteTransferService.config.ENTITY_TYPE;
    const previousRequest =
      this.stateService.getByPath(
        ['entities', entityType, 'previousRequest'],
        defaultRequestParams,
      ) ?? defaultRequestParams;

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public edit(): void {
    const status = this.data?.status;

    this.router.navigate(
      [IncompleteTransferService.config.uiRoute, get(this, 'data._id', '')],
      {
        queryParams: {
          get_updated_items: includes(
            this.transferService.needGetUpdateItemsStatus,
            status,
          )
            ? true
            : null,
        },
      },
    );
  }

  public async updateStatusWithDMSync(
    status: TRANSFER_STATUS_VALUE,
  ): Promise<boolean> {
    const statusMessage = UPDATE_STATUS_MSG_MAPPING[status];

    const currentDocument =
      await this.transferService.getDocumentByIdWithSkipUpdateDocument(
        this.data._id,
      );

    if (
      isEmpty(currentDocument.updated_items) &&
      isEmpty(currentDocument.unavailable_items)
    ) {
      const message = this.i18nService.instant(
        I18n.ConfirmMessages.AreYouWantToBulkUpdateTarget,
        generateBulkUpdateConfirmMessageParam({
          action: statusMessage.action,
          did: statusMessage.did,
          i18nService: this.i18nService,
        }),
      );
      const result = await this.uiActions.confirm({
        text: message,
      });
      if (!result.confirmed) {
        return false;
      }
      await this.changeTransferStatusById(status);

      return true;
    }
    this.edit();

    return true;
  }

  public async cancelTransfer(): Promise<boolean> {
    const currentDocument =
      await this.transferService.getDocumentByIdWithSkipUpdateDocument(
        this.data._id,
      );
    if (
      !isEmpty(currentDocument.updated_items) ||
      !isEmpty(currentDocument.unavailable_items)
    ) {
      await this.transferService.acknowledgeDocument(this.data._id);
    }

    const message = this.i18nService.translate(
      I18n.ConfirmMessages.AreYouWantToBulkUpdateTarget,
      generateBulkUpdateConfirmMessageParam({
        action: I18n.Common.cancel,
        i18nService: this.i18nService,
      }),
    );

    const result = await this.uiActions.confirm({
      text: message,
    });
    if (!result.confirmed) {
      return false;
    }
    await this.changeTransferStatusById(TRANSFER_STATUS_VALUE.Canceled);
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
      case COMMON_ACTION_TYPES.Edit:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Accept:
        this.updateStatusWithDMSync(TRANSFER_STATUS_VALUE.Completed);
        break;
      case COMMON_ACTION_TYPES.Submit:
        this.updateStatusWithDMSync(TRANSFER_STATUS_VALUE.Pending);
        break;
      case COMMON_ACTION_TYPES.Cancel:
        this.cancelTransfer();
        break;
      case COMMON_ACTION_TYPES.Reject:
        this.updateStatusWithDMSync(TRANSFER_STATUS_VALUE.Rejected);
        break;

      default:
        break;
    }
  }

  public async changeTransferStatusById(
    status: TRANSFER_STATUS_VALUE,
  ): Promise<void> {
    await this.apiActions.updateEntityAsPromised(
      ENTITY_TYPES.TRANSFER,
      { status },
      { endpoint: this.data._id },
    );
    this.refresh({});
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  private initActionButtons(): void {
    const isPreEvent: boolean = isEqual(
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
      this.data.transfer_type,
    );

    this.actionButtons = cloneDeep(TRANSFER_ACTION_BUTTONS) as ActionButton[];

    forEach(this.actionButtons, (btn) => {
      switch (btn.type) {
        case COMMON_ACTION_TYPES.View:
          const permissionCheckName = get(
            this.incompleteTransferService,
            'config.entityNameToCheckPermissions',
            IncompleteTransferService.ENTITY_TYPE,
          );
          btn.visible =
            !this.permissionService.canManage(permissionCheckName) &&
            this.permissionService.canView(permissionCheckName);
          break;
        case COMMON_ACTION_TYPES.Edit:
          btn.visible = true;
          break;
        case COMMON_ACTION_TYPES.Submit:
        case COMMON_ACTION_TYPES.Cancel:
          btn.visible = isPreEvent;
          break;
        case COMMON_ACTION_TYPES.Reject:
        case COMMON_ACTION_TYPES.Accept:
          btn.visible = !isPreEvent;
          break;
        default:
          break;
      }
    });
  }
}
